/**
 * Service Health Card Component
 *
 * Displays health status for a single service with expand/collapse functionality.
 */

import { useState } from 'react';
import type { ServiceHealthStatus } from '../../../utils/health-api';
import { getStatusText, formatTimestamp } from '../../../utils/health-api';
import { HealthCheckDetail } from '../HealthCheckDetail';
import { Surface } from '../../atoms/Surface';
import { Button } from '../../atoms/Button';
import { StatusIndicator } from '../../atoms/StatusIndicator';
import { Heading } from '../../atoms/Heading';
import { Text } from '../../atoms/Text';
import { Stack } from '../../layout/Stack';

export interface ServiceHealthCardProps {
  service: ServiceHealthStatus;
}

const statusMap: Record<string, 'pass' | 'warn' | 'fail' | 'unknown'> = {
  pass: 'pass',
  warn: 'warn',
  fail: 'fail',
};

export function ServiceHealthCard({ service }: ServiceHealthCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const checks = service.response?.checks
    ? Object.values(service.response.checks).flat()
    : [];

  const hasChecks = checks.length > 0;
  const summary = service.response?.summary;
  const statusVariant = statusMap[service.status] || 'unknown';

  return (
    <Surface level="surface1" p={0} className="overflow-hidden">
      <Button
        variant="ghost"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-4 h-auto rounded-none hover:bg-muted/5"
        aria-expanded={isExpanded}
        aria-controls={`service-details-${service.serviceId}`}
      >
        <Stack direction="horizontal" spacing={4} align="center" justify="between" className="w-full">
          <Stack direction="horizontal" spacing={4} align="center" className="min-w-0 flex-1">
            <StatusIndicator
              status={statusVariant}
              size="md"
              label={getStatusText(service.status)}
            />

            <Stack spacing={1} className="text-left min-w-0 flex-1">
              <Heading level="h3" className="text-lg">
                {service.serviceName}
              </Heading>
              <Text variant="caption">
                {getStatusText(service.status)}
                {service.lastChecked && (
                  <span className="ml-2">• {formatTimestamp(service.lastChecked)}</span>
                )}
              </Text>
            </Stack>
          </Stack>

          <Stack direction="horizontal" spacing={4} align="center">
            {summary && (
              <Stack direction="horizontal" spacing={3} className="hidden sm:flex text-sm">
                {summary.passed > 0 && (
                  <Text variant="caption" className="text-success">
                    {summary.passed} passed
                  </Text>
                )}
                {summary.failed > 0 && (
                  <Text variant="caption" className="text-danger">
                    {summary.failed} failed
                  </Text>
                )}
                {summary.warned > 0 && (
                  <Text variant="caption" className="text-warning">
                    {summary.warned} warned
                  </Text>
                )}
              </Stack>
            )}

            {hasChecks && (
              <svg
                className={`w-5 h-5 transition-transform text-muted ${
                  isExpanded ? 'rotate-180' : ''
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            )}
          </Stack>
        </Stack>
      </Button>

      {isExpanded && (
        <div id={`service-details-${service.serviceId}`} className="border-t border-border">
          {service.error && (
            <div className="px-6 py-4 bg-danger/10 border-b border-danger/20">
              <Text variant="caption" className="text-danger">
                <span className="font-medium">Error:</span> {service.error}
              </Text>
            </div>
          )}

          {service.response && (
            <div className="px-6 py-3 bg-muted/5 border-b border-border">
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                {service.response.serviceId && (
                  <>
                    <Text variant="caption" as="dt">Service ID:</Text>
                    <Text variant="caption" as="dd" className="font-mono">
                      {service.response.serviceId}
                    </Text>
                  </>
                )}
                {service.response.version && (
                  <>
                    <Text variant="caption" as="dt">Version:</Text>
                    <Text variant="caption" as="dd" className="font-mono">
                      {service.response.version}
                    </Text>
                  </>
                )}
                {service.response.timestamp && (
                  <>
                    <Text variant="caption" as="dt">Last Check:</Text>
                    <Text variant="caption" as="dd">
                      {new Date(service.response.timestamp).toLocaleString()}
                    </Text>
                  </>
                )}
              </dl>
            </div>
          )}

          {hasChecks ? (
            <div className="divide-y divide-border">
              {checks.map((check, index) => (
                <HealthCheckDetail key={`${check.id}-${index}`} check={check} />
              ))}
            </div>
          ) : (
            <div className="px-6 py-8 text-center">
              <Text variant="muted">
                {service.error ? 'Unable to load health checks' : 'No health checks available'}
              </Text>
            </div>
          )}
        </div>
      )}
    </Surface>
  );
}

export default ServiceHealthCard;
